import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useEffect, useMemo, useState } from "react";
import { AppsContainer } from "@lib/components/AppComponents";
import { useBlockchainTransactionQueries } from "@lib/apiQueries";
import { AppContext, SharedContextProps } from "@lib/types/Context";
import { ROUTES } from "@lib/constants";
import { getRoute } from "@lib/helpers/RouteHelper";
import { getPaymentProviderDetailTabs } from "@app/shared/paymentProviderDetail/Tabs";
import {
  DateRangeFilter,
  ProviderLogo,
} from "@lib/components/MiniComponents/Others";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGetProvider, apiUpdateProvider } from "@lib/api/provider";
import { ProviderModal } from "@lib/components/PageComponents/Provider";
import {
  SidebarPaymentProviderDetailListItems,
  SidebarPaymentSummaryListItems,
} from "./SidebarUtils";
import { useDateRange } from "@lib/hooks/useDateRange";
import { ApiParams } from "@lib/types";

const coverImg = "/assets/images/wall/v-card.jpg";

const SharedPaymentProviderDetail: React.FC<SharedContextProps> = ({
  context = AppContext.PAYMENT_PROVIDER,
  customTitle,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const Tabs = getPaymentProviderDetailTabs();
  const { providerId, tab = Tabs[0].key } = useParams();

  useEffect(() => {
    if (!providerId) {
      toast.error("provider ID is undefined");
      navigate(ROUTES.ADMIN.PAYMENT_PROVIDER.LIST);
    }
  }, [providerId]);

  const providerQuery = useQuery({
    queryFn: () => apiGetProvider(providerId),
    queryKey: ["provider", providerId],
    enabled: !!providerId,
  });
  const { data, isLoading, isFetching } = providerQuery;
  const apiData = data?.data;

  const [selectedTab, setSelectedTab] = useState<TabKeys>(tab || Tabs[0].key);
  const _keys = Tabs.map((tab) => tab.key);
  type TabKeys = (typeof _keys)[number];

  useEffect(() => {
    handleTabClick(tab || Tabs[0].key);
  }, [tab]);

  const handleTabClick = (key: TabKeys) => {
    setSelectedTab(key);
    navigate(
      getRoute(ROUTES.ADMIN.PAYMENT_PROVIDER.DETAIL_TAB, {
        providerId,
        tab: key,
      }),
    );
  };
  const [openModal, setModalOpen] = useState(false);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const queryClient = useQueryClient();
  const updateQuery = useMutation({
    mutationFn: apiUpdateProvider,
    mutationKey: ["provider"],
    onSuccess() {
      toast.success(
        <FormattedMessage
          id="common.updatedMessage"
          values={{ name: messages["common.provider"] as string }}
        />,
      );
      queryClient.invalidateQueries({
        queryKey: ["provider"],
      });
    },
  });

  const { dateBetween, setDateBetween } = useDateRange({
    initialRange: "monthly",
  });
  const params = useMemo(() => {
    return {
      page: 1,
      limit: 1000,
      sort: "createdAt,DESC",
      s: [
        {
          $and: [
            { createdAt: { $gte: dateBetween[0] } },
            { createdAt: { $lte: dateBetween[1] } },
          ],
        },
      ],
    } as ApiParams;
  }, [dateBetween]);

  const { blockchainTransactionsQuery } = useBlockchainTransactionQueries({
    messages,
    enabled: !!apiData?.id && !!params && !isLoading,
    queryKey: [
      "provider-transactions-summary",
      { id: apiData?.id },
      JSON.stringify(params),
    ],
    params,
  });
  const {
    data: blockchainTransactionsData,
    isLoading: blockchainTransactionsLoading,
  } = blockchainTransactionsQuery;
  const blockchainTransactionsApiData = blockchainTransactionsData?.data?.data;

  const title = customTitle || (messages["provider.detail"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      isLoading={isLoading || isFetching}
      breadcrumbProps={{
        urlParams: {
          providerId: `${apiData?.name}`,
        },
      }}
      sidebarContent={
        <AppsContainer.Sidebar>
          <AppsContainer.SidebarUserCard
            cover={<img src={coverImg} alt="" />}
            avatar={<ProviderLogo provider={apiData} size="large" />}
            title={apiData?.name}
            editAction={() => toggle()}
            listItems={SidebarPaymentProviderDetailListItems({
              provider: apiData,
              isLoading: isLoading || isFetching,
            })}
            loading={isLoading || isFetching}
          />
          <AppsContainer.SidebarUserCard
            header={{
              title: messages["common.accountSummary"] as string,
            }}
            footer={
              <DateRangeFilter value={dateBetween} onChange={setDateBetween} />
            }
            listItems={SidebarPaymentSummaryListItems({
              blockchainTransactionsData: blockchainTransactionsApiData,
              isLoading: isLoading || blockchainTransactionsLoading,
            })}
            loading={isLoading || blockchainTransactionsLoading}
          />
        </AppsContainer.Sidebar>
      }
      sidebarProviderProps={{ doNotTrack: true }}
      tabs={{
        tabs: Tabs.map((tab) => ({
          ...tab,
          isActive: selectedTab === tab.key,
          onClick: () => handleTabClick(tab.key),
          loading: isLoading || isFetching,
          props: {
            providerId: providerId,
            paymentProvider: apiData,
            pageIsActive: selectedTab === tab.key,
            withoutContainer: true,
            context: context,
            detailContext: context,
            minusHeight: 50,
          },
        })),
        selectedTab: selectedTab,
        onChange: (key: string) => handleTabClick(key as TabKeys),
      }}
    >
      <ProviderModal
        mutationQuery={updateQuery}
        openModal={openModal}
        toggleModal={toggle}
        provider={apiData}
      />
    </AppsContainer>
  );
};

export default SharedPaymentProviderDetail;
